// components/header/tools-panel.component.tsx
//
// The compact Tools dropdown that replaces the 109-link mega-menu.
// Layout: [search] → [popular chips] → [category tiles] → [see-all link]
//
// Search hits the same backend endpoint as <HeaderSearch /> (Lucene index in
// professorx). When the query is empty, the popular + categories view shows;
// while typing, results render inline below the input.

import React, { Component, createRef } from 'react';
import Router from 'next/router';
import { Subscription } from 'rxjs';

import { lazyInject } from '../../context/module';
import { ApiConfig } from '../../services/api-config';
import { LightHttp } from '../../services/light-http.service';
import { mediaToolsConfig } from '../../commons/media-tools.config';
import styles from './tools-panel.component.module.scss';

interface SearchResult {
    slug: string;
    url: string;
    title: string;
    category: string;
}

interface SearchResponse {
    results: SearchResult[];
    query: string;
    count: number;
}

// Top 6 GSC-popular tools (impressions from research/lite-pages-backlog.md).
// Hand-curated — refresh periodically.
const POPULAR_TOOLS: Array<{ name: string; href: string }> = [
    { name: 'Compress GIF', href: '/compress-gif' },
    { name: 'Compress MP4', href: '/compress-mp4' },
    { name: 'Merge Image → PDF', href: '/merge-image-to-pdf' },
    { name: 'Audio Compressor', href: '/audio-compressor' },
    { name: 'Compress MP3', href: '/compress-mp3' },
    { name: 'Compress PDF', href: '/compress-pdf' },
];

// Pull category tiles from the existing media-tools.config. Skip "all-tools"
// (footer link covers it) and "utility-tools" (no real hub page on QA).
// Keep "other-tools" — it's the 300+ developer/format/encode/validate hub,
// which is the destination users want when they look for JSON / regex / JWT
// / base64 / minify / hash etc. An earlier draft deep-linked tiles to single
// tools (Developer Tools → /json-formatter); replaced with the real hub.
const PRIMARY_CATEGORIES = mediaToolsConfig.filter((c) => !['all-tools', 'utility-tools'].includes(c.slug));

interface Props {
    /** Called when the panel should close (user clicked a link, hit Esc, etc.) */
    onClose: () => void;
}

interface State {
    query: string;
    results: SearchResult[];
    isSearching: boolean;
    activeIndex: number;
}

export class ToolsPanel extends Component<Props, State> {
    @lazyInject(LightHttp) private lightHttp: LightHttp;
    @lazyInject(ApiConfig) private apiConfig: ApiConfig;

    private inputRef = createRef<HTMLInputElement>();
    private searchTimeout: ReturnType<typeof setTimeout>;
    private searchSubscription: Subscription;
    private navigating = false;

    state: State = {
        query: '',
        results: [],
        isSearching: false,
        activeIndex: -1,
    };

    componentDidMount() {
        // Auto-focus the search input on open — the most common path is "I know
        // what I want, just let me type" — making the field the default action.
        // Defer one frame so the slide-in animation doesn't fight focus.
        requestAnimationFrame(() => this.inputRef.current?.focus());
        document.addEventListener('keydown', this.onDocumentKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.onDocumentKeyDown);
        clearTimeout(this.searchTimeout);
        this.searchSubscription?.unsubscribe();
    }

    onDocumentKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') this.props.onClose();
    };

    onQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        this.setState({ query, activeIndex: -1 });

        clearTimeout(this.searchTimeout);
        this.searchSubscription?.unsubscribe();

        if (query.length < 2) {
            this.setState({ results: [], isSearching: false });
            return;
        }
        this.setState({ isSearching: true });

        this.searchTimeout = setTimeout(() => {
            this.searchSubscription = this.lightHttp.get<SearchResponse>(
                this.apiConfig.getFeatureSearchEndpoint(query),
            ).subscribe({
                next: (response) => this.setState({ results: response.results || [], isSearching: false }),
                error: () => this.setState({ isSearching: false }),
            });
        }, 250);
    };

    onInputKeyDown = (e: React.KeyboardEvent) => {
        const { results, activeIndex } = this.state;
        if (results.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.setState({ activeIndex: Math.min(activeIndex + 1, results.length - 1) });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.setState({ activeIndex: Math.max(activeIndex - 1, 0) });
        } else if (e.key === 'Enter' && activeIndex >= 0) {
            e.preventDefault();
            this.navigateTo(results[activeIndex].url);
        }
    };

    navigateTo = (url: string) => {
        if (this.navigating) return;
        this.navigating = true;
        Router.push(url).then(() => {
            this.navigating = false;
            this.props.onClose();
        });
    };

    private renderSearchInput() {
        const { query, isSearching } = this.state;
        return (
            <div className={styles.searchRow}>
                <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                    ref={this.inputRef}
                    className={styles.searchInput}
                    type="text"
                    placeholder="Search 5000+ tools…"
                    value={query}
                    onChange={this.onQueryChange}
                    onKeyDown={this.onInputKeyDown}
                    aria-label="Search tools"
                />
                {isSearching && <div className={styles.spinner} aria-hidden="true" />}
                {query && (
                    <button
                        type="button"
                        className={styles.clearBtn}
                        onClick={() => { this.setState({ query: '', results: [], activeIndex: -1 }); this.inputRef.current?.focus(); }}
                        aria-label="Clear search"
                    >×</button>
                )}
            </div>
        );
    }

    private renderResults() {
        const { results, isSearching, query, activeIndex } = this.state;
        if (results.length > 0) {
            return (
                <div className={styles.results} role="listbox">
                    {results.slice(0, 10).map((r, i) => (
                        <a
                            key={r.slug}
                            href={r.url}
                            className={`${styles.resultItem} ${i === activeIndex ? styles.resultActive : ''}`}
                            onClick={(e) => { e.preventDefault(); this.navigateTo(r.url); }}
                            onMouseEnter={() => this.setState({ activeIndex: i })}
                            role="option"
                            aria-selected={i === activeIndex}
                        >
                            <span className={styles.resultTitle}>{r.title}</span>
                            <span className={`${styles.resultBadge} ${styles[`cat${r.category}`] || ''}`}>{r.category}</span>
                        </a>
                    ))}
                    {results.length > 10 && (
                        <a className={styles.resultsMore} href={`/tools?q=${encodeURIComponent(query)}`} onClick={() => this.props.onClose()}>
                            See all {results.length} results →
                        </a>
                    )}
                </div>
            );
        }
        if (!isSearching && query.length >= 2) {
            return <div className={styles.empty}>No tools found for &ldquo;{query}&rdquo;.</div>;
        }
        return null;
    }

    private renderBrowse() {
        return (
            <>
                <div className={styles.sectionLabel}>Popular</div>
                <div className={styles.popularRow}>
                    {POPULAR_TOOLS.map((t) => (
                        <a
                            key={t.href}
                            href={t.href}
                            className={styles.popularChip}
                            onClick={() => this.props.onClose()}
                        >{t.name}</a>
                    ))}
                </div>

                <div className={styles.sectionLabel}>Browse by category</div>
                <div className={styles.tileGrid}>
                    {PRIMARY_CATEGORIES.map((c) => (
                        <a
                            key={c.slug}
                            href={`/${c.slug}`}
                            className={styles.categoryTile}
                            onClick={() => this.props.onClose()}
                        >
                            <span className={styles.tileIcon} aria-hidden="true">{c.icon}</span>
                            <span className={styles.tileName}>{c.name}</span>
                        </a>
                    ))}
                </div>
            </>
        );
    }

    render() {
        const showResults = this.state.query.length >= 2;
        return (
            <div className={styles.panel} role="dialog" aria-label="Tools menu">
                {this.renderSearchInput()}
                {showResults ? this.renderResults() : this.renderBrowse()}
                <a className={styles.footerLink} href="/tools" onClick={() => this.props.onClose()}>
                    See all 5000+ tools →
                </a>
            </div>
        );
    }
}
